/**
 * <LanguageChip /> — globe icon dropdown in the header.
 *
 * Stores the user's preferred display language in localStorage under
 * `sp:pref-lang` (ISO-639-1 code). `<TranslatableText />` reads that key
 * first, falls back to `navigator.language`.
 *
 * We don't fire any backend calls here — the chip is a pure UI preference
 * store. It also broadcasts a `pref-lang-changed` event so open pages can
 * re-render translations without a full reload.
 */
import { useEffect, useState } from "react";
import { Globe, Check } from "lucide-react";
import { http } from "@/lib/api";
import i18n from "i18next";

export const PREF_LANG_KEY = "sp:pref-lang";
export const PREF_LANG_EVENT = "pref-lang-changed";

// Curated list — the same POPULAR_TARGETS as TranslatableText, kept in sync.
// `label` is what shows on the pill: the code for known languages, or a short
// glyph for AUTO. We keep it compact so the caption fits on one line.
const LANGS = [
  { code: "auto", name: "Browser default", native: "" },
  { code: "en", name: "English", native: "English" },
  { code: "es", name: "Spanish", native: "Español" },
  { code: "fr", name: "French", native: "Français" },
  { code: "de", name: "German", native: "Deutsch" },
  { code: "pt", name: "Portuguese", native: "Português" },
  { code: "it", name: "Italian", native: "Italiano" },
  { code: "ru", name: "Russian", native: "Русский" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "mr", name: "Marathi", native: "मराठी" },
  { code: "bn", name: "Bengali", native: "বাংলা" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
  { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ur", name: "Urdu", native: "اردو" },
  { code: "ar", name: "Arabic", native: "العربية" },
  { code: "fa", name: "Persian", native: "فارسی" },
  { code: "he", name: "Hebrew", native: "עברית" },
  { code: "zh", name: "Chinese", native: "中文" },
  { code: "ja", name: "Japanese", native: "日本語" },
  { code: "ko", name: "Korean", native: "한국어" },
  { code: "th", name: "Thai", native: "ไทย" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia" },
  { code: "tr", name: "Turkish", native: "Türkçe" },
  { code: "sw", name: "Swahili", native: "Kiswahili" },
  { code: "uk", name: "Ukrainian", native: "Українська" },
];

export function getPreferredLang() {
  try {
    const v = localStorage.getItem(PREF_LANG_KEY);
    if (v && v !== "auto") return v;
  } catch { /* private-mode etc. */ }
  try {
    return (navigator.language || navigator.userLanguage || "en").toLowerCase().split("-")[0];
  } catch { return "en"; }
}

export default function LanguageChip() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("auto");
  const [signedIn, setSignedIn] = useState(false);

  // On mount: hydrate from localStorage first (works for guests instantly),
  // then try /api/auth/me — if signed in and the server has a saved language,
  // that trumps localStorage. If localStorage differs, we push it up to the
  // server as the source of truth (so language preference sticks across
  // devices for signed-in users).
  useEffect(() => {
    try { setCurrent(localStorage.getItem(PREF_LANG_KEY) || "auto"); } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get("/auth/me");
        if (cancelled) return;
        setSignedIn(true);
        const serverLang = r.data?.user?.preferred_language;
        let localLang = "auto";
        try { localLang = localStorage.getItem(PREF_LANG_KEY) || "auto"; } catch { /* ignore */ }
        if (serverLang && serverLang !== localLang) {
          // Server wins — apply it locally.
          try { localStorage.setItem(PREF_LANG_KEY, serverLang); } catch { /* ignore */ }
          setCurrent(serverLang);
          try { window.dispatchEvent(new CustomEvent(PREF_LANG_EVENT, { detail: { code: serverLang } })); } catch { /* ignore */ }
        } else if (!serverLang && localLang !== "auto") {
          // First-time signed-in user: push their local pref up to the server.
          http.patch("/auth/me/language", { lang: localLang }).catch(() => {});
        }
      } catch {
        // Guest — localStorage is the only source. Nothing to do.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pick = (code) => {
    try {
      if (code === "auto") localStorage.removeItem(PREF_LANG_KEY);
      else localStorage.setItem(PREF_LANG_KEY, code);
    } catch { /* ignore */ }
    setCurrent(code);
    setOpen(false);
    // Swap the react-i18next active language too so the site chrome
    // (nav labels, CTAs, headings) instantly translates.
    try {
      const effective = code === "auto" ? (navigator.language || "en").toLowerCase().split("-")[0] : code;
      i18n.changeLanguage(effective);
    } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(PREF_LANG_EVENT, { detail: { code } })); } catch { /* ignore */ }
    // Best-effort persist server-side for signed-in users.
    if (signedIn) {
      http.patch("/auth/me/language", { lang: code }).catch(() => {});
    }
  };

  const active = LANGS.find((l) => l.code === current) || LANGS[0];
  // Show the language name (native form when available) so people who don't
  // read English still see something they can recognise. Falls back to
  // "Language" for the default AUTO state.
  const pillLabel =
    active.code === "auto"
      ? "Language"
      : (active.native && active.native !== active.name ? active.native : active.name);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition hover:shadow-sm"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)", color: "var(--wofy-text)" }}
        title="Choose your language"
        data-testid="lang-chip"
        aria-label={`Change preferred language — currently ${active.name}`}
      >
        <Globe className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
        <span className="font-semibold" dir="auto">{pillLabel}</span>
        {active.code !== "auto" && (
          <span className="font-mono uppercase opacity-60 border-l pl-1.5 ml-0.5" style={{ borderColor: "var(--wofy-border)" }}>
            {active.code}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 z-50 rounded-xl shadow-xl border text-sm w-64 overflow-y-auto"
            style={{ background: "#FDFBF7", borderColor: "var(--wofy-border)", maxHeight: "70vh" }}
            data-testid="lang-chip-menu"
          >
            {/* Sticky header — pins to top when the option list scrolls. */}
            <div
              className="sticky top-0 z-10 border-b"
              style={{ borderColor: "var(--wofy-border)", background: "#FDFBF7" }}
            >
              <div className="px-3 pt-3 pb-1 overline" style={{ color: "var(--wofy-text-soft)" }}>PREFERRED LANGUAGE</div>
              <p className="px-3 pb-3 text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
                User-generated content will offer to translate into this language everywhere on the site.
              </p>
              {current !== "en" && current !== "auto" && (
                <button
                  type="button"
                  onClick={() => pick("en")}
                  className="w-full px-3 py-2 text-left text-xs font-semibold border-t hover:bg-black/[0.04]"
                  style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)", color: "var(--wofy-primary)" }}
                  data-testid="lang-chip-reset-english"
                >
                  ↺ Reset to English
                </button>
              )}
            </div>
            <div className="py-1">
              {LANGS.map((l) => {
                const isActive = l.code === current;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => pick(l.code)}
                    dir="auto"
                    className="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-black/[0.04]"
                    style={{ background: "#FDFBF7" }}
                    data-testid={`lang-chip-option-${l.code}`}
                  >
                    <span>
                      <span className="font-medium">{l.name}</span>
                      {l.native && l.native !== l.name && <span className="opacity-60 ml-2 text-xs">{l.native}</span>}
                    </span>
                    {isActive && <Check className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
